/**
 * Utility function to apply a replacement string to content based on selection context.
 * 
 * This is used for AI edits where the AI returns just a replacement string
 * instead of the full document, reducing token usage and latency.
 * 
 * @param content The current full content
 * @param selection The selection context (text, prefix, suffix)
 * @param replacement The replacement text from AI
 * @returns Object with patched content and replacement position, or null if patching failed
 */
export function applyReplacement(
    content: string,
    selection: { text: string; prefix: string; suffix: string },
    replacement: string
): { content: string; replacementStart: number; replacementEnd: number } | null {
    const { text, prefix, suffix } = selection;
    
    console.log('[applyReplacement] Starting patch application', {
        contentLength: content.length,
        text: JSON.stringify(text),
        prefix: JSON.stringify(prefix),
        suffix: JSON.stringify(suffix),
        replacementSnippet: JSON.stringify(replacement.substring(0, 50))
    });

    // Strategy 1: Try to find exact match with full context
    const contextKey = prefix + text + suffix;
    const contextIndex = content.indexOf(contextKey);

    if (contextIndex !== -1) {
        // Found precise match using prefix+text+suffix
        const prefixEnd = contextIndex + prefix.length;
        const textEnd = prefixEnd + text.length;

        console.log('[applyReplacement] Strategy 1 (Full Context) Success', {
            contextIndex,
            prefixEnd,
            textEnd
        });

        const patchedContent = content.substring(0, prefixEnd) + replacement + content.substring(textEnd);
        return {
            content: patchedContent,
            replacementStart: prefixEnd,
            replacementEnd: prefixEnd + replacement.length
        };
    }

    console.log('[applyReplacement] Strategy 1 Failed, trying Strategy 2 (Prefix+Text)');

    // Strategy 2: Try matching just prefix+text (suffix might have changed)
    const prefixTextKey = prefix + text;
    const prefixTextIndex = content.indexOf(prefixTextKey);

    if (prefixTextIndex !== -1) {
        const prefixEnd = prefixTextIndex + prefix.length;
        const textEnd = prefixEnd + text.length;

        console.log('[applyReplacement] Strategy 2 (Prefix+Text) Success', {
            prefixTextIndex,
            prefixEnd,
            textEnd
        });

        const patchedContent = content.substring(0, prefixEnd) + replacement + content.substring(textEnd);
        return {
            content: patchedContent,
            replacementStart: prefixEnd,
            replacementEnd: prefixEnd + replacement.length
        };
    }

    console.log('[applyReplacement] Strategy 2 Failed, trying Strategy 3 (Unique Text)');

    // Strategy 3: If text is unique, use that alone
    const firstIndex = content.indexOf(text);
    const lastIndex = content.lastIndexOf(text);

    if (firstIndex !== -1 && firstIndex === lastIndex) {
        // Text appears exactly once - safe to replace
        console.log('[applyReplacement] Strategy 3 (Unique Text) Success', {
            firstIndex,
            textLength: text.length
        });

        const patchedContent = content.substring(0, firstIndex) + replacement + content.substring(firstIndex + text.length);
        return {
            content: patchedContent,
            replacementStart: firstIndex,
            replacementEnd: firstIndex + replacement.length
        };
    }

    // Failed to find a safe match
    console.warn('[applyReplacement] ALL strategies failed to patch.', {
        firstIndex,
        lastIndex,
        contentSnippetAroundCursor: JSON.stringify(content.substring(Math.max(0, contextIndex - 100), Math.min(content.length, contextIndex + 100)))
    });
    return null;
}
